import { useState, useEffect } from 'react'
import { aiConfigApi } from '../services/aiConfig'
import { extractErrorMessage } from '../utils/errorUtils'
import {
  PageHeader,
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  LoadingSpinner,
  ErrorMessage,
  Pagination,
} from '../components/common'
import { RefreshCw, DollarSign, Zap, Clock, Hash, CheckCircle } from 'lucide-react'

const AIUsage = () => {
  const [logs, setLogs] = useState([])
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [from, setFrom] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 30)
    return d.toISOString().split('T')[0]
  })
  const [to, setTo] = useState(() => new Date().toISOString().split('T')[0])
  const [serviceTypeFilter, setServiceTypeFilter] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 50

  useEffect(() => {
    loadData()
  }, [from, to, serviceTypeFilter])

  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      const params = { from, to }
      if (serviceTypeFilter) params.serviceType = serviceTypeFilter

      const [logsRes, summaryRes] = await Promise.allSettled([
        aiConfigApi.getUsageLogs(params),
        aiConfigApi.getUsageSummary(params),
      ])

      if (logsRes.status === 'fulfilled') setLogs(logsRes.value.data)
      if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value.data)
      setPage(1)
    } catch (err) {
      setError(extractErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const formatCost = (cost) => {
    if (cost == null) return '$0.00'
    return `$${Number(cost).toFixed(4)}`
  }

  const formatNumber = (num) => {
    if (num == null) return '0'
    return num.toLocaleString()
  }

  const formatDuration = (ms) => {
    if (!ms) return '—'
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(1)}s`
  }

  const formatLatency = (ms) => {
    if (!ms) return '0ms'
    return `${Math.round(ms).toLocaleString()}ms`
  }

  const paginatedLogs = logs.slice((page - 1) * pageSize, page * pageSize)
  const totalPages = Math.ceil(logs.length / pageSize)

  const serviceTypes = [
    'Announcement', 'AnnouncementShort', 'AnnouncementMakeUp',
    'Delivery', 'EmailTemplate', 'SmsTemplate', 'ImageExtraction', 'Transcription'
  ]

  if (loading && !summary) return <LoadingSpinner />

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI Usage Dashboard"
        subtitle="Monitor AI model usage, costs, and performance"
        actions={
          <Button variant="ghost" onClick={loadData}>
            <RefreshCw size={16} className="mr-2" /> Refresh
          </Button>
        }
      />

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

      {/* Filters */}
      <Card>
        <CardBody>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs font-medium text-dark-500 mb-1">From</label>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="px-3 py-2 border border-dark-200 rounded-button text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-dark-500 mb-1">To</label>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="px-3 py-2 border border-dark-200 rounded-button text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-dark-500 mb-1">Service Type</label>
              <select
                value={serviceTypeFilter}
                onChange={(e) => setServiceTypeFilter(e.target.value)}
                className="px-3 py-2 border border-dark-200 rounded-button text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              >
                <option value="">All</option>
                {serviceTypes.map(st => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <SummaryCard
            icon={Hash}
            label="Total Calls"
            value={formatNumber(summary.totalCalls)}
            color="text-primary-500"
          />
          <SummaryCard
            icon={CheckCircle}
            label="Success Rate"
            value={`${summary.successRate ?? 0}%`}
            color="text-accent-600"
          />
          <SummaryCard
            icon={Zap}
            label="Total Tokens"
            value={formatNumber(summary.totalTokens)}
            color="text-blue-500"
          />
          <SummaryCard
            icon={Clock}
            label="Avg Latency"
            value={formatLatency(summary.averageLatencyMs)}
            color="text-indigo-500"
          />
          <SummaryCard
            icon={DollarSign}
            label="Estimated Cost"
            value={formatCost(summary.totalEstimatedCost)}
            color="text-primary-500"
          />
        </div>
      )}

      {/* Usage by Service */}
      {summary?.usageByService && summary.usageByService.length > 0 && (
        <Card>
          <CardHeader title="Usage by Service" />
          <CardBody padding="none">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-dark-700/10">
                    <th className="text-left px-6 py-3 text-xs font-semibold text-dark-400 uppercase">Service</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-dark-400 uppercase">Calls</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-dark-400 uppercase">Total Tokens</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-dark-400 uppercase">Avg Latency</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-dark-400 uppercase">Est. Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-700/5">
                  {summary.usageByService.map((svc) => (
                    <tr key={svc.serviceType} className="hover:bg-dark-50/50">
                      <td className="px-6 py-3 text-sm font-medium">{svc.serviceType}</td>
                      <td className="px-6 py-3 text-sm text-right">{formatNumber(svc.callCount)}</td>
                      <td className="px-6 py-3 text-sm text-right">{formatNumber(svc.totalTokens)}</td>
                      <td className="px-6 py-3 text-sm text-right">{formatLatency(svc.averageLatencyMs)}</td>
                      <td className="px-6 py-3 text-sm text-right font-mono">{formatCost(svc.estimatedCost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Detailed Logs */}
      <Card>
        <CardHeader title={`Usage Logs (${logs.length})`} />
        <CardBody padding="none">
          {logs.length === 0 ? (
            <div className="px-6 py-12 text-center text-dark-400">
              No usage logs found for the selected period.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-dark-700/10">
                      <th className="text-left px-6 py-3 text-xs font-semibold text-dark-400 uppercase">Time</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-dark-400 uppercase">Service</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-dark-400 uppercase">Model</th>
                      <th className="text-right px-6 py-3 text-xs font-semibold text-dark-400 uppercase">Tokens</th>
                      <th className="text-right px-6 py-3 text-xs font-semibold text-dark-400 uppercase">Latency</th>
                      <th className="text-center px-6 py-3 text-xs font-semibold text-dark-400 uppercase">Status</th>
                      <th className="text-right px-6 py-3 text-xs font-semibold text-dark-400 uppercase">Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-dark-700/5">
                    {paginatedLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-dark-50/50 transition-colors">
                        <td className="px-6 py-3 text-xs text-dark-500 whitespace-nowrap">
                          {new Date(log.createdAt).toLocaleString()}
                        </td>
                        <td className="px-6 py-3 text-sm">{log.serviceTypeName}</td>
                        <td className="px-6 py-3">
                          <code className="text-xs font-mono">{log.modelId.split('.').pop()}</code>
                        </td>
                        <td className="px-6 py-3 text-sm text-right">{formatNumber(log.totalTokens)}</td>
                        <td className="px-6 py-3 text-sm text-right">{formatDuration(log.durationMs)}</td>
                        <td className="px-6 py-3 text-center">
                          <Badge variant={log.success ? 'success' : 'danger'} size="sm">
                            {log.success ? 'OK' : 'Fail'}
                          </Badge>
                        </td>
                        <td className="px-6 py-3 text-sm text-right font-mono">{formatCost(log.estimatedCost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-dark-700/10">
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    totalItems={logs.length}
                  />
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

const SummaryCard = ({ icon: Icon, label, value, color }) => (
  <Card>
    <CardBody>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-dark-50 ${color}`}>
          <Icon size={20} />
        </div>
        <div>
          <div className="text-xs text-dark-400 uppercase tracking-wider">{label}</div>
          <div className="text-xl font-bold text-dark-800">{value}</div>
        </div>
      </div>
    </CardBody>
  </Card>
)

export default AIUsage
